import { useEffect, useState } from 'react'

import classNames from 'classnames'

import Fixture from '~/components/atoms/Fixture/Fixture'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { ComponentTheme } from '~/components/themes'

import styles from './MetabaseEmbed.module.css'

const METABASE_SITE_URL = process.env.NEXT_PUBLIC_METABASE_SITE_URL || ''

export interface MetabaseEmbedProps {
  resourceId: number
  resourceType: 'dashboard' | 'question'
  params: Record<string, string>[]
  chartTitle?: string
  chartSummary?: string
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  theme: ComponentTheme
}

export default function MetabaseEmbed({
  resourceId,
  resourceType,
  chartTitle,
  chartSummary,
  verticalPadding,
  noPaddingBottom,
  theme,
  params,
}: MetabaseEmbedProps) {
  const [iframeUrl, setIframeUrl] = useState<string>()

  useEffect(() => {
    async function fetchEmbedUrl() {
      try {
        // Call the API endpoint to generate the token server-side
        const response = await fetch('/api/metabase-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resourceId,
            resourceType,
            params,
          }),
        })

        if (!response.ok) {
          console.error('Failed to generate Metabase token')
          return
        }

        const { token } = await response.json()

        // Construct the iframe URL with the token
        const url = `${METABASE_SITE_URL}/embed/${resourceType}/${token}#theme=light&bordered=false&titled=false`
        setIframeUrl(url)
      } catch (error) {
        console.error('Error fetching Metabase embed URL:', error)
      }
    }

    fetchEmbedUrl()
  }, [resourceId, resourceType, params])

  return (
    <div className={styles.base}>
      <Fixture
        theme={theme}
        verticalPadding={verticalPadding}
        noPaddingBottom={noPaddingBottom}
      >
        <div
          className={classNames(
            styles.chartHeader,
            (chartTitle || chartSummary) && styles.chartHeaderPadded
          )}
        >
          {chartTitle && (
            <Text Tag="h3" size={TextSizes.primaryTitleLrg}>
              {chartTitle}
            </Text>
          )}
          {chartSummary && (
            <Text Tag="p" size={TextSizes.bodyMed}>
              {chartSummary}
            </Text>
          )}
        </div>

        <div className={styles.embed}>
          {iframeUrl && (
            <iframe src={iframeUrl} frameBorder={0} width="100%" height={500} />
          )}
        </div>
      </Fixture>
    </div>
  )
}
